/**
 * Functional Status Assessment Component
 * 
 * Embeddable component for documenting patient functional status during visits.
 * Automatically triggers LCD eligibility alerts when thresholds are crossed.
 * 
 * Features:
 * - KPS/PPS score entry with visual scale
 * - FAST staging for dementia patients
 * - NYHA classification for heart failure
 * - Auto-triggers alerts via WebSocket
 * - Integrates with visit documentation workflow
 */

import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Activity, AlertTriangle, CheckCircle, ChevronDown, ChevronUp,
  Heart, Brain, Loader2, TrendingDown, TrendingUp, Minus,
  ClipboardCheck, Bell, Save
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// KPS Scale descriptions
const KPS_SCALE = [
  { score: 100, label: "Normal, no complaints", category: "Able to perform normal activity" },
  { score: 90, label: "Minor signs/symptoms", category: "Able to perform normal activity" },
  { score: 80, label: "Normal activity with effort", category: "Able to perform normal activity" },
  { score: 70, label: "Cares for self, unable to work", category: "Unable to work, can live at home" },
  { score: 60, label: "Requires occasional assistance", category: "Unable to work, can live at home" },
  { score: 50, label: "Requires considerable assistance", category: "Unable to work, can live at home" },
  { score: 40, label: "Disabled, requires special care", category: "Unable to care for self" },
  { score: 30, label: "Severely disabled, hospitalization indicated", category: "Unable to care for self" },
  { score: 20, label: "Very sick, active supportive treatment", category: "Unable to care for self" },
  { score: 10, label: "Moribund, fatal processes progressing", category: "Unable to care for self" },
  { score: 0, label: "Dead", category: "Dead" }
];

// FAST Stages
const FAST_STAGES = [
  { stage: "1", description: "No difficulty" },
  { stage: "2", description: "Subjective forgetfulness" },
  { stage: "3", description: "Decreased job functioning" },
  { stage: "4", description: "Difficulty with complex tasks" },
  { stage: "5", description: "Requires assistance choosing clothes" },
  { stage: "6a", description: "Difficulty dressing properly" },
  { stage: "6b", description: "Unable to bathe properly" },
  { stage: "6c", description: "Unable to handle toileting" },
  { stage: "6d", description: "Urinary incontinence" },
  { stage: "6e", description: "Fecal incontinence" },
  { stage: "7a", description: "Speech limited to ~6 words/day" },
  { stage: "7b", description: "Speech limited to ~1 word/day" },
  { stage: "7c", description: "Ambulatory ability lost", hospice: true },
  { stage: "7d", description: "Ability to sit lost", hospice: true },
  { stage: "7e", description: "Ability to smile lost", hospice: true },
  { stage: "7f", description: "Ability to hold head up lost", hospice: true }
];

// NYHA Classifications
const NYHA_CLASSES = [
  { class: "I", description: "No limitation of physical activity" },
  { class: "II", description: "Slight limitation - comfortable at rest" },
  { class: "III", description: "Marked limitation - comfortable only at rest" },
  { class: "IV", description: "Unable to carry on any physical activity without discomfort", hospice: true }
];

export default function FunctionalStatusAssessment({
  patientId,
  patientName,
  diagnosis, // To determine which scales to show
  previousAssessment, // Last documented assessment for comparison
  onAssessmentComplete,
  onAlertGenerated,
  compact = false,
  isDark = true
}) {
  const token = localStorage.getItem("token");
  
  // Assessment states
  const [isOpen, setIsOpen] = useState(!compact);
  const [kpsScore, setKpsScore] = useState(previousAssessment?.kps || 70);
  const [ppsScore, setPpsScore] = useState(previousAssessment?.pps || 70);
  const [fastStage, setFastStage] = useState(previousAssessment?.fast || "");
  const [nyhaClass, setNyhaClass] = useState(previousAssessment?.nyha || "");
  const [notes, setNotes] = useState("");
  const [includeKPS, setIncludeKPS] = useState(true);
  const [includePPS, setIncludePPS] = useState(true);
  const [includeFAST, setIncludeFAST] = useState(diagnosis?.toLowerCase()?.includes("dementia"));
  const [includeNYHA, setIncludeNYHA] = useState(diagnosis?.toLowerCase()?.includes("heart") || diagnosis?.toLowerCase()?.includes("chf"));
  
  // UI states
  const [saving, setSaving] = useState(false);
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [pendingAlerts, setPendingAlerts] = useState([]);
  const [savedAssessment, setSavedAssessment] = useState(null);

  // Calculate change from previous
  const kpsChange = previousAssessment?.kps ? kpsScore - previousAssessment.kps : 0;
  const ppsChange = previousAssessment?.pps ? ppsScore - previousAssessment.pps : 0;

  // Get KPS description
  const getKPSDescription = (score) => {
    const item = KPS_SCALE.find(k => k.score === score);
    return item ? item.label : "";
  };

  // Check if hospice threshold met
  const isHospiceThreshold = () => {
    if (includeKPS && kpsScore <= 70) return true;
    if (includePPS && ppsScore <= 70) return true;
    if (includeFAST && fastStage) {
      const hospiceStages = ["7c", "7d", "7e", "7f"];
      if (hospiceStages.includes(fastStage.toLowerCase())) return true;
    }
    if (includeNYHA && nyhaClass === "IV") return true;
    return false;
  };

  // Save assessment and trigger alerts
  const saveAssessment = async () => {
    if (!patientId) {
      toast.error("Patient ID required");
      return;
    }

    try {
      setSaving(true);
      const alerts = [];

      // Save KPS assessment
      if (includeKPS) {
        const res = await fetch(`${API_URL}/api/lcd-alerts/assess`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            patient_id: patientId,
            assessment_type: "kps",
            score: kpsScore,
            assessed_by: "current-user",
            notes: notes
          })
        });

        if (res.ok) {
          const data = await res.json();
          if (data.alerts_generated > 0) {
            alerts.push(...data.alerts);
          }
        }
      }

      // Save PPS assessment
      if (includePPS) {
        const res = await fetch(`${API_URL}/api/lcd-alerts/assess`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            patient_id: patientId,
            assessment_type: "pps",
            score: ppsScore,
            assessed_by: "current-user",
            notes: notes
          })
        });

        if (res.ok) {
          const data = await res.json();
          if (data.alerts_generated > 0) {
            alerts.push(...data.alerts);
          }
        }
      }

      // Save FAST assessment
      if (includeFAST && fastStage) {
        const res = await fetch(`${API_URL}/api/lcd-alerts/assess`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            patient_id: patientId,
            assessment_type: "fast",
            stage: fastStage,
            assessed_by: "current-user",
            notes: notes
          })
        });

        if (res.ok) {
          const data = await res.json();
          if (data.alerts_generated > 0) {
            alerts.push(...data.alerts);
          }
        }
      }

      // Save NYHA assessment
      if (includeNYHA && nyhaClass) {
        const res = await fetch(`${API_URL}/api/lcd-alerts/assess`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            patient_id: patientId,
            assessment_type: "nyha",
            classification: nyhaClass,
            assessed_by: "current-user",
            notes: notes
          })
        });

        if (res.ok) {
          const data = await res.json();
          if (data.alerts_generated > 0) {
            alerts.push(...data.alerts);
          }
        }
      }

      // Store saved assessment data
      const assessment = {
        kps: includeKPS ? kpsScore : null,
        pps: includePPS ? ppsScore : null,
        fast: includeFAST ? fastStage : null,
        nyha: includeNYHA ? nyhaClass : null,
        notes,
        savedAt: new Date().toISOString()
      };
      setSavedAssessment(assessment);

      // Handle alerts
      if (alerts.length > 0) {
        setPendingAlerts(alerts);
        setShowAlertDialog(true);
        onAlertGenerated?.(alerts);
      } else {
        toast.success("Functional assessment documented successfully");
      }

      onAssessmentComplete?.(assessment);

    } catch (error) {
      console.error("Assessment save error:", error);
      toast.error("Failed to save assessment");
    } finally {
      setSaving(false);
    }
  };

  // Render change indicator
  const renderChangeIndicator = (change) => {
    if (change === 0) return <Minus className="w-4 h-4 text-slate-400" />;
    if (change > 0) return <TrendingUp className="w-4 h-4 text-emerald-400" />;
    return <TrendingDown className="w-4 h-4 text-red-400" />;
  };

  // Compact view
  if (compact && !isOpen) {
    return (
      <div 
        className={`p-3 rounded-lg border cursor-pointer transition-all hover:border-blue-500/50 ${
          isDark ? "bg-slate-800/50 border-slate-700" : "bg-gray-50 border-gray-200"
        }`}
        onClick={() => setIsOpen(true)}
        data-testid="functional-assessment-compact"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-blue-400" />
            <span className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
              Functional Status Assessment
            </span>
          </div>
          <div className="flex items-center gap-2">
            {savedAssessment ? (
              <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">Documented</Badge>
            ) : isHospiceThreshold() ? (
              <Badge className="bg-amber-500/20 text-amber-400 text-xs">Below Threshold</Badge>
            ) : (
              <Badge variant="outline" className="text-xs">Not Documented</Badge>
            )}
            <ChevronDown className="w-4 h-4 text-slate-400" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card className={`${isDark ? "bg-slate-900 border-slate-800" : "bg-white"}`} data-testid="functional-assessment-card">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-slate-800/50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isDark ? "bg-blue-500/20" : "bg-blue-100"}`}>
                  <Activity className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <CardTitle className="text-base">Functional Status Assessment</CardTitle>
                  <CardDescription>Document KPS, PPS, FAST, NYHA scores</CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {isHospiceThreshold() && (
                  <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Hospice Threshold
                  </Badge>
                )}
                {savedAssessment && (
                  <Badge className="bg-emerald-500/20 text-emerald-400">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Saved
                  </Badge>
                )}
                {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="space-y-6">
            {/* Assessment Options */}
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox id="include-kps" checked={includeKPS} onCheckedChange={setIncludeKPS} />
                <Label htmlFor="include-kps" className="text-sm">KPS</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="include-pps" checked={includePPS} onCheckedChange={setIncludePPS} />
                <Label htmlFor="include-pps" className="text-sm">PPS</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="include-fast" checked={includeFAST} onCheckedChange={setIncludeFAST} />
                <Label htmlFor="include-fast" className="text-sm flex items-center gap-1">
                  <Brain className="w-3 h-3" /> FAST (Dementia)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="include-nyha" checked={includeNYHA} onCheckedChange={setIncludeNYHA} />
                <Label htmlFor="include-nyha" className="text-sm flex items-center gap-1">
                  <Heart className="w-3 h-3" /> NYHA (Heart)
                </Label>
              </div>
            </div>

            {/* KPS Assessment */}
            {includeKPS && (
              <div className={`p-4 rounded-lg border ${
                kpsScore <= 70 
                  ? "bg-amber-500/10 border-amber-500/30" 
                  : isDark ? "bg-slate-800 border-slate-700" : "bg-gray-50 border-gray-200"
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <Label className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-blue-400" />
                    Karnofsky Performance Scale (KPS)
                  </Label>
                  <div className="flex items-center gap-2">
                    <span className={`text-2xl font-bold ${kpsScore <= 70 ? "text-amber-400" : "text-blue-400"}`}>
                      {kpsScore}
                    </span>
                    {previousAssessment?.kps && (
                      <div className="flex items-center gap-1">
                        {renderChangeIndicator(kpsChange)}
                        <span className={`text-xs ${kpsChange < 0 ? "text-red-400" : kpsChange > 0 ? "text-emerald-400" : "text-slate-400"}`}>
                          {kpsChange > 0 ? "+" : ""}{kpsChange}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <Slider
                  value={[kpsScore]}
                  onValueChange={(v) => setKpsScore(v[0])}
                  min={0}
                  max={100}
                  step={10}
                  className="mb-2"
                />
                <div className="flex justify-between items-center">
                  <span className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                    {getKPSDescription(kpsScore)}
                  </span>
                  <Badge variant={kpsScore <= 70 ? "default" : "outline"} className={kpsScore <= 70 ? "bg-amber-500" : ""}>
                    {kpsScore <= 70 ? "Hospice Eligible (≤70)" : "Above Threshold"}
                  </Badge>
                </div>
              </div>
            )}

            {/* PPS Assessment */}
            {includePPS && (
              <div className={`p-4 rounded-lg border ${
                ppsScore <= 70 
                  ? "bg-amber-500/10 border-amber-500/30" 
                  : isDark ? "bg-slate-800 border-slate-700" : "bg-gray-50 border-gray-200"
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <Label className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-purple-400" />
                    Palliative Performance Scale (PPS)
                  </Label>
                  <div className="flex items-center gap-2">
                    <span className={`text-2xl font-bold ${ppsScore <= 70 ? "text-amber-400" : "text-purple-400"}`}>
                      {ppsScore}%
                    </span>
                    {previousAssessment?.pps && (
                      <div className="flex items-center gap-1">
                        {renderChangeIndicator(ppsChange)}
                        <span className={`text-xs ${ppsChange < 0 ? "text-red-400" : ppsChange > 0 ? "text-emerald-400" : "text-slate-400"}`}>
                          {ppsChange > 0 ? "+" : ""}{ppsChange}%
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <Slider
                  value={[ppsScore]}
                  onValueChange={(v) => setPpsScore(v[0])}
                  min={0}
                  max={100}
                  step={10}
                  className="mb-2"
                />
                <div className="flex justify-between items-center">
                  <span className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                    Palliative performance percentage
                  </span>
                  <Badge variant={ppsScore <= 70 ? "default" : "outline"} className={ppsScore <= 70 ? "bg-amber-500" : ""}>
                    {ppsScore <= 70 ? "Hospice Eligible (≤70%)" : "Above Threshold"}
                  </Badge>
                </div>
              </div>
            )}

            {/* FAST Assessment */}
            {includeFAST && (
              <div className={`p-4 rounded-lg border ${
                ["7c", "7d", "7e", "7f"].includes(fastStage?.toLowerCase())
                  ? "bg-amber-500/10 border-amber-500/30" 
                  : isDark ? "bg-slate-800 border-slate-700" : "bg-gray-50 border-gray-200"
              }`}>
                <Label className="flex items-center gap-2 mb-3">
                  <Brain className="w-4 h-4 text-purple-400" />
                  FAST Stage (Dementia)
                </Label>
                <Select value={fastStage} onValueChange={setFastStage}>
                  <SelectTrigger className={isDark ? "bg-slate-900 border-slate-700" : ""}>
                    <SelectValue placeholder="Select FAST stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {FAST_STAGES.map((item) => (
                      <SelectItem key={item.stage} value={item.stage}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Stage {item.stage}</span>
                          <span className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                            - {item.description}
                          </span>
                          {item.hospice && (
                            <Badge className="bg-amber-500/20 text-amber-400 text-xs ml-2">Hospice</Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {["7c", "7d", "7e", "7f"].includes(fastStage?.toLowerCase()) && (
                  <p className="text-xs text-amber-400 mt-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Stage {fastStage} meets hospice eligibility criteria for dementia
                  </p>
                )}
              </div>
            )}

            {/* NYHA Assessment */}
            {includeNYHA && (
              <div className={`p-4 rounded-lg border ${
                nyhaClass === "IV"
                  ? "bg-amber-500/10 border-amber-500/30" 
                  : isDark ? "bg-slate-800 border-slate-700" : "bg-gray-50 border-gray-200"
              }`}>
                <Label className="flex items-center gap-2 mb-3">
                  <Heart className="w-4 h-4 text-red-400" />
                  NYHA Classification (Heart Failure)
                </Label>
                <Select value={nyhaClass} onValueChange={setNyhaClass}>
                  <SelectTrigger className={isDark ? "bg-slate-900 border-slate-700" : ""}>
                    <SelectValue placeholder="Select NYHA class" />
                  </SelectTrigger>
                  <SelectContent>
                    {NYHA_CLASSES.map((item) => (
                      <SelectItem key={item.class} value={item.class}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Class {item.class}</span>
                          <span className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                            - {item.description}
                          </span>
                          {item.hospice && (
                            <Badge className="bg-amber-500/20 text-amber-400 text-xs ml-2">Hospice</Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {nyhaClass === "IV" && (
                  <p className="text-xs text-amber-400 mt-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Class IV meets hospice eligibility criteria for heart disease
                  </p>
                )}
              </div>
            )}

            {/* Notes */}
            <div>
              <Label className="text-xs mb-2 block">Assessment Notes (Optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add clinical observations about functional status..."
                className={`min-h-[80px] ${isDark ? "bg-slate-800 border-slate-700" : ""}`}
              />
            </div>

            {/* Hospice Threshold Warning */}
            {isHospiceThreshold() && (
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-400">Hospice Eligibility Threshold Met</p>
                    <p className={`text-sm mt-1 ${isDark ? "text-slate-300" : "text-gray-600"}`}>
                      This patient&apos;s functional status meets CMS hospice eligibility criteria.
                      An alert will be generated when this assessment is saved.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <ClipboardCheck className="w-4 h-4" />
                <span>Assessment will trigger eligibility alerts if thresholds are crossed</span>
              </div>
              <Button 
                onClick={saveAssessment}
                disabled={saving || (!includeKPS && !includePPS && !includeFAST && !includeNYHA)}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Assessment
              </Button>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>

      {/* Alert Generated Dialog */}
      <AlertDialog open={showAlertDialog} onOpenChange={setShowAlertDialog}>
        <AlertDialogContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-400" />
              Eligibility Alert Generated
            </AlertDialogTitle>
            <AlertDialogDescription>
              Based on the documented functional status, the following eligibility alerts have been generated:
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-3 my-4">
            {pendingAlerts.map((alert, idx) => (
              <div 
                key={idx}
                className={`p-3 rounded-lg border ${
                  alert.severity === "critical" 
                    ? "bg-red-500/10 border-red-500/30" 
                    : "bg-amber-500/10 border-amber-500/30"
                }`}
              >
                <p className={`font-medium ${
                  alert.severity === "critical" ? "text-red-400" : "text-amber-400"
                }`}>
                  {alert.title}
                </p>
                <p className={`text-sm mt-1 ${isDark ? "text-slate-300" : "text-gray-600"}`}>
                  {alert.message}
                </p>
                <div className="flex gap-2 mt-2">
                  <Badge variant="outline" className="text-xs">
                    Current: {alert.current_value}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    Threshold: {alert.threshold_value}
                  </Badge>
                </div>
              </div>
            ))}
          </div>

          <AlertDialogFooter>
            <AlertDialogAction className="bg-emerald-600 hover:bg-emerald-700">
              <CheckCircle className="w-4 h-4 mr-2" />
              Understood
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
